import React, { FC, useEffect, useRef } from 'react';
import { JSX } from 'react-dom';
import { StyleProp, TextStyle } from 'react-native';
import styled, { css, useTheme } from 'styled-components/native';
import AsteriskIcon from '../../../../assets/icons/asterisk.svg';
import { TextInputProps, I18nManager } from 'react-native';
import { calcHeight, calcWidth, isWeb } from '../../../../utils/dimensions';
import { ITheme } from '../../../../themes/defaultTheme';
import { IS_ANDROID } from '../../../../utils/constants/env';

interface IProps extends TextInputProps {
  RightComponent?: JSX.Element;
  LeftComponent?: JSX.Element;
  noTopRadius?: boolean;
  noBottomRadius?: boolean;
  isMandatory?: boolean;
  hasError?: boolean;
  noErrorOnBorder?: boolean;
  placeholder?: string;
  placeholderTextColor?: string;
  focused?: boolean;
  inputStyle?: StyleProp<TextStyle>;
  dataSet?: { [key: string]: string };
}

const TextInputV2: FC<IProps> = (props) => {
  const { RightComponent, LeftComponent, style, isMandatory, inputStyle, dataSet, ...restProps } = props;
  const inputElementRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    inputElementRef.current?.setNativeProps({
      style: { fontFamily: theme.fontFamilies.GilroyMedium },
    });
  });

  return (
    <S.InputWrapper autoCompleteType='off' autoCorrect={false} {...restProps} style={style}>
      <S.LeftComponentWrapper>
        {LeftComponent && LeftComponent}
        {isMandatory && (
          <S.MandatoryIconWrapper>
            <AsteriskIcon width={calcWidth(8)} height={calcHeight(8)} />
          </S.MandatoryIconWrapper>
        )}
      </S.LeftComponentWrapper>

      {/* TODO: styled-component types don't match with react types */}
      {/* @ts-ignore */}
      <S.Input ref={inputElementRef} style={inputStyle} dataSet={dataSet} {...restProps} />

      {RightComponent && RightComponent}
    </S.InputWrapper>
  );
};

const InputWrapper = styled.View(
  ({ theme, noBottomRadius, noTopRadius, hasError, noErrorOnBorder }: IProps & ITheme) => `
	flex-direction: row;
	align-items: center;
	border-width: 1px;
	border-style: solid;
	border-color: ${theme.colors.lightGray15};
	background-color: ${theme.colors.white};
	height: ${calcHeight(IS_ANDROID ? 47 : 44)}px;
	width: ${calcWidth(298)}px;

	${!noBottomRadius && !noTopRadius ? 'border-radius: 51px;' : ''}
	${noBottomRadius && !noTopRadius ? 'border-top-left-radius: 51px; border-top-right-radius: 51px;' : ''}
	${!noBottomRadius && noTopRadius ? 'border-bottom-left-radius: 51px; border-bottom-right-radius: 51px;' : ''}

	${hasError && !noErrorOnBorder ? `border-width: 2px; border-color: ${theme.colors.warning}` : ''}
	padding-right: ${calcWidth(20)}px;
	padding-left: ${calcWidth(20)}px;
`,
);

const Input = styled.TextInput(
  ({ theme, LeftComponent, RightComponent, hasError }: IProps & ITheme) => css`
    width: 100%;
    font-size: ${theme.fontSizes.s16}px;
    color: ${hasError ? theme.colors.warning : theme.colors.darkPrimary};
    flex: 1;
    ${LeftComponent ? `margin-left: ${calcWidth(10)}px;` : ''}
    ${RightComponent ? `margin-right: ${calcWidth(10)}px;` : ''}
    text-align: ${I18nManager.isRTL && !isWeb ? 'right' : 'left'};
    padding-vertical: 0;
  `,
);

const LeftComponentWrapper = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: ${calcWidth(40)}px;
`;

const MandatoryIconWrapper = styled.View`
  margin-right: ${calcWidth(8)}px;
`;

const S = {
  Input,
  InputWrapper,
  LeftComponentWrapper,
  MandatoryIconWrapper,
};

export default TextInputV2;
